import type { TokenStore } from "@/domains/tokens/token-store"
import { BodyLg } from "@/domains/design/components/body-lg"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { cn } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

export interface DepositTokenInputViewProps {
  label: string
  token: TokenStore
  amount: string
  balanceLabel: string
  onBalanceClick: () => void
  onFocus: () => void
  onChangeAmount: (value: string) => void
  isDisabled: boolean
}

/**
 * Deposit Token Input View
 * Pure view component (no store access, no hooks).
 * Mirrors the Swap TokenInputCardView layout but without token selection.
 */
export function DepositTokenInputView({
  label,
  token,
  amount,
  balanceLabel,
  onBalanceClick,
  onFocus,
  onChangeAmount,
  isDisabled,
}: DepositTokenInputViewProps) {
  const getFontSize = (value: string | undefined): string => {
    if (!value) return "text-4xl"
    const length = value.length
    if (length > 18) return "text-xl"
    if (length > 12) return "text-2xl"
    if (length > 9) return "text-3xl"
    return "text-4xl"
  }

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-p400-light bg-p400 p-4 transition-all focus-within:border-p100 hover:border-p300-light",
        isDisabled ? "opacity-60" : "",
      )}
    >
      {/* Header Row: Label + Balance */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-bold tracking-wide text-t100">{label}</span>
        <button
          type="button"
          onClick={onBalanceClick}
          disabled={isDisabled}
          className={cn(
            "text-xs font-medium text-t200 transition-colors hover:text-t100",
            isDisabled ? "cursor-not-allowed" : "cursor-pointer",
          )}
        >
          {balanceLabel}
        </button>
      </div>

      {/* Main Row: Token (Left) + Amount (Right) */}
      <div className="flex items-start justify-between gap-4">
        {/* Left: Fixed Token Pill */}
        <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-p300-light bg-p300 py-2 pl-3 pr-3 shadow-sm">
          <div className="from-purple-500 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br to-blue-500">
            <DisplayTokens tokens={[token]} tokenSize={24} />
          </div>
          <BodyLg className="font-bold tracking-tight text-t100">{token.symbol}</BodyLg>
        </div>

        {/* Right: Amount */}
        <div className="flex min-w-0 flex-1 flex-col items-end">
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onFocus={onFocus}
            onChange={(e) => onChangeAmount(e.target.value)}
            placeholder="0"
            disabled={isDisabled}
            className={`w-full bg-transparent text-right font-bold text-t100 outline-none placeholder:text-t300 ${getFontSize(amount)} transition-all duration-200 ${isDisabled ? "cursor-not-allowed" : ""}`}
          />
          <BodyXl As="span" className="mt-1 max-w-full truncate text-xs font-medium text-t200">
            {token.name}
          </BodyXl>
        </div>
      </div>
    </div>
  )
}
